import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';

const { FiTool, FiArrowRight, FiFilter, FiInfo, FiTarget, FiSettings } = FiIcons;

const Linear = () => {
  const { products } = useDatabase();
  const [selectedAccuracy, setSelectedAccuracy] = useState('all');

  // Filter nur Lineartechnik (EC000924)
  const linearProducts = products.filter(p => p.etim_class_code === 'EC000924');

  const accuracyClasses = {
    'all': 'Alle Genauigkeiten',
    'N': 'Normal (N)',
    'H': 'Hoch (H)', 
    'P': 'Präzision (P)',
    'SP': 'Super-Präzision (SP)',
    'UP': 'Ultra-Präzision (UP)'
  };

  const filteredProducts = selectedAccuracy === 'all' 
    ? linearProducts 
    : linearProducts.filter(p => p.specifications.accuracy_class === selectedAccuracy);

  const getAccuracyDescription = (accuracy) => {
    const descriptions = {
      'N': 'Standardgenauigkeit für allgemeine Anwendungen. Wirtschaftliche Lösung für normale Anforderungen.',
      'H': 'Erhöhte Genauigkeit für präzisere Positionierung. Gute Balance zwischen Kosten und Leistung.',
      'P': 'Präzisionsklasse für anspruchsvolle Anwendungen. Hohe Laufruhe und Positioniergenauigkeit.',
      'SP': 'Super-Präzision für hochgenaue Maschinen. Minimale Toleranzen und exzellente Laufruhe.',
      'UP': 'Ultra-Präzision für Messmaschinen und Halbleiterproduktion. Höchste Genauigkeitsanforderungen.'
    };
    return descriptions[accuracy] || 'Hochwertige Linearführung für industrielle Anwendungen.';
  };

  const getAccuracySpecs = (accuracy) => {
    const specs = {
      'N': { straightness: '±15 μm', height: '±20 μm', width: '±30 μm' },
      'H': { straightness: '±7 μm', height: '±12 μm', width: '±20 μm' },
      'P': { straightness: '±3 μm', height: '±5 μm', width: '±8 μm' },
      'SP': { straightness: '±2 μm', height: '±3 μm', width: '±5 μm' },
      'UP': { straightness: '±1 μm', height: '±2 μm', width: '±3 μm' }
    };
    return specs[accuracy];
  };

  const linearTypes = [
    {
      type: 'Profilschienenführung',
      description: 'Hochbelastbare Führungen mit Wälzkörperumlauf für höchste Tragkraft und Steifigkeit',
      features: ['Hohe Tragkraft', 'Alle Richtungen belastbar', 'Kompakte Bauform', 'Wartungsarm'],
      applications: ['CNC-Bearbeitung', 'Robotik', 'Automatisierung', 'Werkzeugmaschinen']
    },
    {
      type: 'Rundwellenführung',
      description: 'Kostengünstige Lösung für einfache Linearanwendungen mit gehärteten Wellen',
      features: ['Kostengünstig', 'Einfache Montage', 'Selbstausrichtend', 'Hohe Geschwindigkeit'],
      applications: ['Pneumatikzylinder', 'Handhabung', 'Verpackung', 'Sortierung']
    }
  ];

  const preloadClasses = {
    'Z0': 'Ohne Vorspannung (Standard)',
    'ZA': 'Leichte Vorspannung',
    'ZB': 'Mittlere Vorspannung', 
    'ZC': 'Starke Vorspannung'
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Lineartechnik</h1>
            <p className="text-gray-600 mt-2">
              Präzisions-Linearführungen für industrielle Automatisierung und Werkzeugmaschinen
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-100">
            <SafeIcon icon={FiTool} className="w-5 h-5" />
            <span className="font-medium">{linearProducts.length} Führungen</span>
          </div>
        </div>

        {/* Linear Types Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {linearTypes.map((type, index) => (
            <motion.div
              key={type.type}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiTool} className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{type.type}</h3>
                  <p className="text-gray-600 text-sm mb-3">{type.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Eigenschaften</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {type.features.map((feature, i) => (
                          <li key={i}>• {feature}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Anwendungen</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {type.applications.map((app, i) => (
                          <li key={i}>• {app}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Accuracy Class Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiTarget} className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Genauigkeitsklassen</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
            {Object.entries(accuracyClasses).map(([code, name]) => (
              <button
                key={code}
                onClick={() => setSelectedAccuracy(code)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedAccuracy === code
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          
          {/* Accuracy Specifications */}
          {selectedAccuracy !== 'all' && getAccuracySpecs(selectedAccuracy) && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800 mb-3">
                {getAccuracyDescription(selectedAccuracy)}
              </p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-blue-900">Geradheit:</span>
                  <p className="text-blue-800">{getAccuracySpecs(selectedAccuracy).straightness}</p>
                </div>
                <div>
                  <span className="font-semibold text-blue-900">Höhentoleranz:</span>
                  <p className="text-blue-800">{getAccuracySpecs(selectedAccuracy).height}</p>
                </div>
                <div>
                  <span className="font-semibold text-blue-900">Breitentoleranz:</span>
                  <p className="text-blue-800">{getAccuracySpecs(selectedAccuracy).width}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiTool} className="w-6 h-6 text-gray-400" />
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    product.availability === 'In Stock' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.availability === 'In Stock' ? 'Verfügbar' : 'Nicht verfügbar'}
                  </span>
                  {product.specifications.accuracy_class && (
                    <div className="mt-1">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        Klasse {product.specifications.accuracy_class}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 mb-1">
                {product.manufacturer_sku}
              </h3>
              <p className="text-sm text-gray-600 mb-3">{product.manufacturer}</p>

              <div className="space-y-2 mb-4">
                {product.specifications.size && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Baugröße:</span>
                    <span className="font-medium">{product.specifications.size}</span>
                  </div>
                )}
                {product.specifications.load_ratings?.dynamic_C_N && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">C dyn:</span>
                    <span className="font-medium">{(product.specifications.load_ratings.dynamic_C_N / 1000).toFixed(1)} kN</span>
                  </div>
                )}
                {product.specifications.load_ratings?.static_C0_N && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">C₀ stat:</span>
                    <span className="font-medium">{(product.specifications.load_ratings.static_C0_N / 1000).toFixed(1)} kN</span>
                  </div>
                )}
                {product.specifications.preload_class && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Vorspannung:</span>
                    <span className="font-medium">{product.specifications.preload_class}</span>
                  </div>
                )}
              </div>

              <Link
                to={`/product/${product.id}`}
                className="flex items-center justify-between w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                <span>Details anzeigen</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Technical Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Technische Grundlagen</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Tragzahlberechnung</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Dynamische Tragzahl C nach ISO 14728</li>
                <li>• Statische Tragzahl C₀</li>
                <li>• Momentbelastbarkeit M₀ₓ, M₀ᵧ, M₀z</li>
                <li>• Lebensdauer L bei gegebener Belastung</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Vorspannungsklassen</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                {Object.entries(preloadClasses).map(([code, desc]) => (
                  <li key={code}>• <strong>{code}:</strong> {desc}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Schmierung & Wartung</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Lebensdauerfettschmierung standard</li>
                <li>• Zentralschmierung für Dauerbetrieb</li>
                <li>• Abstreifer gegen Verschmutzung</li>
                <li>• Überwachung von Spiel und Laufruhe</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Linear;